import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../components/LanguageToggle';
import { useAuth } from '../contexts/AuthContext';
import { Volleyball, Eye, EyeSlash } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const LOGO_URL = "/logo-tk.png";

const categories = [
  { value: 'iniciante', label: 'Iniciante' },
  { value: 'intermediário', label: 'Intermediário' },
  { value: 'avançado', label: 'Avançado' }
];

const DIA_ABBR = { 'Segunda': 'Seg', 'Terca': 'Ter', 'Terça': 'Ter', 'Quarta': 'Qua', 'Quinta': 'Qui', 'Sexta': 'Sex', 'Sabado': 'Sab', 'Sábado': 'Sab', 'Domingo': 'Dom' };

const PLAN_MAX_TURMAS = { '1x': 1, '2x': 2, '3x': 3 };

// Funcional turma IDs are prefixed with "func-". Everything else is Futevolei.
const isFuncionalTurma = (id) => id.startsWith('func-');
const FUTEVOLEI_PRICE_BY_COUNT = { 0: 0, 1: 90, 2: 170, 3: 200 };
const FUNCIONAL_PRICE_BY_COUNT = { 0: 0, 1: 70, 2: 100, 3: 100 };
const CARD_FEE_RATE = 0.05;

export default function Register() {
  const { t } = useTranslation();
  const categoriesLocal = [
    { value: 'iniciante', label: t('register.iniciante') },
    { value: 'intermediário', label: t('register.intermediario') },
    { value: 'avançado', label: t('register.avancado') }
  ];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    birth_date: '',
    category: 'iniciante',
    plan: '1x',
    turmas: []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turmaOptions, setTurmaOptions] = useState([]);

  useEffect(() => {
    axios.get(`${API}/turmas/all`).then(({ data }) => {
      const opts = (data.fixas || []).map(t => ({
        value: t.id,
        label: `${DIA_ABBR[t.dia] || t.dia} ${t.horario} - Futevolei ${t.turma}`,
        group: /kids/i.test(t.turma) ? 'Futevolei Kids' : 'Futevolei',
      }));
      setTurmaOptions(opts);
    }).catch(() => {});
  }, []);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const maxTurmas = PLAN_MAX_TURMAS[formData.plan] || 1;

  // Dynamic pricing based on actual selection (Futevolei vs Funcional)
  const futCount = formData.turmas.filter(id => !isFuncionalTurma(id)).length;
  const funcCount = formData.turmas.filter(id => isFuncionalTurma(id)).length;
  const futPrice = FUTEVOLEI_PRICE_BY_COUNT[Math.min(futCount, 3)] || 0;
  const funcPrice = FUNCIONAL_PRICE_BY_COUNT[Math.min(funcCount, 3)] || 0;
  const totalMonthly = futPrice + funcPrice;
  const totalMonthlyCard = totalMonthly * (1 + CARD_FEE_RATE);

  const toggleTurma = (value) => {
    setFormData(prev => {
      if (prev.turmas.includes(value)) {
        return { ...prev, turmas: prev.turmas.filter(t => t !== value) };
      }
      if (prev.turmas.length >= maxTurmas) {
        return prev; // silently blocked
      }
      return { ...prev, turmas: [...prev.turmas, value] };
    });
  };

  const changePlan = (plan) => {
    setFormData(prev => ({
      ...prev,
      plan,
      // trim excess if downgrading
      turmas: prev.turmas.slice(0, PLAN_MAX_TURMAS[plan] || 1)
    }));
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('forgot.minLengthError'));
      return;
    }

    if (formData.turmas.length === 0) {
      setError(t('register.selectTurmaError'));
      return;
    }

    setLoading(true);
    const result = await register({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      phone: formData.phone || null,
      birth_date: formData.birth_date || null,
      category: formData.category,
      plan: formData.plan,
      turmas: formData.turmas,
      turma: formData.turmas[0] || null
    });
    
    if (result.success) {
      navigate('/portal');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      <div className="fixed top-4 right-4 z-50"><LanguageToggle /></div>
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1596489396342-9988b49dbbba?q=80&w=2832&auto=format&fit=crop)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-4 mb-8">
            <img
              src={LOGO_URL}
              alt="Tia Karol Sports"
              className="w-14 h-14 object-contain bg-white rounded-xl p-1"
            />
            <h1 className="font-['Outfit'] text-3xl font-bold">Tia Karol Sports</h1>
          </div>
          <p className="text-xl text-white/90 max-w-md leading-relaxed">
            {t('register.subtitle')}
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FDFBF7]">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img
              src={LOGO_URL}
              alt="Tia Karol Sports"
              className="w-12 h-12 object-contain"
            />
            <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">Tia Karol Sports</h1>
          </div>

          <div className="bg-white border border-[#E6E2D8] rounded-xl p-8">
            <h2 className="font-['Outfit'] text-2xl font-semibold text-[#1A1A1A] mb-2">
              {t('register.title')}
            </h2>
            <p className="text-[#73706A] mb-6">
              {t('register.subtitle')}
            </p>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-[#9B2226] text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-1.5">
                  {t('common.fullName')} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field"
                  placeholder={t('common.yourName')}
                  required
                  data-testid="register-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-1.5">
                  {t('common.email')} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-field"
                  placeholder={t('login.emailPlaceholder')}
                  required
                  data-testid="register-email-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-1.5">
                    {t('common.phone')} *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="(000) 000-0000"
                    required
                    data-testid="register-phone-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1A1A] mb-1.5">
                    {t('register.birthday')}
                  </label>
                  <input
                    type="date"
                    name="birth_date"
                    value={formData.birth_date}
                    onChange={handleChange}
                    className="input-field"
                    data-testid="register-birthdate-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-1.5">
                  {t('register.selectCategory')}
                </label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger data-testid="register-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesLocal.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Plan Selection - quantity only; price is computed below based on activity mix */}
              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-2">
                  {t('register.training')} *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: '1x', label: '1x/semana', sub: '1 aula' },
                    { value: '2x', label: '2x/semana', sub: '2 aulas' },
                    { value: '3x', label: '3x/semana', sub: '3 aulas' }
                  ].map(plan => (
                    <button
                      key={plan.value}
                      type="button"
                      onClick={() => changePlan(plan.value)}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        formData.plan === plan.value
                          ? 'bg-[#E85D04] text-white border-[#E85D04] shadow-sm'
                          : 'bg-white text-[#1A1A1A] border-[#E6E2D8] hover:border-[#E85D04]'
                      }`}
                      data-testid={`plan-${plan.value}`}
                    >
                      <p className="font-semibold text-sm">{plan.label}</p>
                      <p className={`text-[11px] mt-1 ${formData.plan === plan.value ? 'text-white/80' : 'text-[#73706A]'}`}>{plan.sub}</p>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#73706A] mt-2">
                  Pagamento ate o dia 01 do mes. Apos o dia 01, multa de $15. Pagamento via cartao acrescenta 5%.
                </p>
              </div>

              {/* Turmas Selection (multi) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-[#1A1A1A]">
                    {t('register.selectTurma')} *
                  </label>
                  <span className={`text-xs font-semibold ${formData.turmas.length === maxTurmas ? 'text-[#2D6A4F]' : 'text-[#73706A]'}`} data-testid="register-turmas-counter">
                    {formData.turmas.length} / {maxTurmas}
                  </span>
                </div>
                <p className="text-xs text-[#73706A] mb-2">
                  {t('register.selectTurmaHint')}
                </p>
                <div className="grid grid-cols-1 gap-2 max-h-72 overflow-y-auto border border-[#E6E2D8] rounded-lg p-2 bg-white">
                  {['Futevolei', 'Futevolei Kids'].map(group => {
                    const items = turmaOptions.filter(o => o.group === group);
                    if (items.length === 0) return null;
                    return (
                      <div key={group}>
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-[#73706A] px-1 pt-2 pb-1 sticky top-0 bg-white" data-testid={`turma-group-${group.replace(/\s+/g,'-').toLowerCase()}`}>{group}</p>
                        {items.map(opt => {
                          const checked = formData.turmas.includes(opt.value);
                          const disabled = !checked && formData.turmas.length >= maxTurmas;
                          return (
                            <label
                              key={opt.value}
                              className={`flex items-center gap-3 px-3 py-2 rounded border cursor-pointer transition-colors mb-1 ${
                                checked
                                  ? 'bg-orange-50 border-[#E85D04]'
                                  : disabled
                                    ? 'bg-[#F5F2EB] border-[#E6E2D8] opacity-50 cursor-not-allowed'
                                    : 'bg-white border-[#E6E2D8] hover:border-[#E85D04]'
                              }`}
                              data-testid={`register-turma-${opt.value}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={disabled}
                                onChange={() => toggleTurma(opt.value)}
                                className="w-4 h-4 accent-[#E85D04]"
                              />
                              <span className="text-sm text-[#1A1A1A]">{opt.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Dynamic Monthly Price Summary - based on actual Funcional/Futevolei mix */}
              {formData.turmas.length > 0 && (
                <div className="border-2 border-[#E85D04] bg-gradient-to-br from-orange-50 to-white rounded-lg p-4 space-y-2" data-testid="register-price-summary">
                  <p className="text-xs uppercase tracking-wider font-bold text-[#E85D04]">Valor mensal</p>
                  {futCount > 0 && (
                    <div className="flex justify-between items-center text-sm" data-testid="price-line-futevolei">
                      <span className="text-[#1A1A1A]">Futevolei ({futCount}x/semana)</span>
                      <span className="font-semibold text-[#1A1A1A]">${futPrice.toFixed(2)}</span>
                    </div>
                  )}
                  {funcCount > 0 && (
                    <div className="flex justify-between items-center text-sm" data-testid="price-line-funcional">
                      <span className="text-[#1A1A1A]">Treinamento Funcional ({funcCount}x/semana)</span>
                      <span className="font-semibold text-[#1A1A1A]">${funcPrice.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-orange-200 pt-2 flex justify-between items-center">
                    <span className="font-semibold text-[#1A1A1A]">Total mensal</span>
                    <span className="font-bold text-2xl font-['Outfit'] text-[#E85D04]" data-testid="register-total-monthly">${totalMonthly.toFixed(2)}</span>
                  </div>
                  <p className="text-[11px] text-[#73706A] text-right">cartão: ${totalMonthlyCard.toFixed(2)} (taxa 5%) · Zelle: ${totalMonthly.toFixed(2)}</p>
                </div>
              )}

              {/* Enrollment Fee removida - alunos pagam somente mensalidades */}

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-1.5">
                  {t('common.password')} *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-field pr-12"
                    placeholder={t('forgot.minLength')}
                    required
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#73706A] hover:text-[#1A1A1A]"
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1A1A1A] mb-1.5">
                  {t('register.passwordConfirm')} *
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="input-field"
                  placeholder={t('forgot.repeatPassword')}
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="register-submit-btn"
              >
                {loading ? t('register.submitting') : t('register.createAccountBtn')}
              </button>
            </form>

            <p className="mt-6 text-center text-[#73706A]">
              {t('register.alreadyAccount')}{' '}
              <Link to="/login" className="text-[#E85D04] hover:text-[#DC2F02] font-medium" data-testid="login-link">
                {t('common.login')}
              </Link>
            </p>
            <p className="mt-3 text-center">
              <Link to="/aula-experimental" className="text-[#0077B6] hover:text-[#03045E] text-sm font-medium" data-testid="trial-class-link">
                Quero agendar uma aula experimental primeiro
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
